/**
 * Pure factory for the `onReorder` callback wired into
 * `<CategoriesScreen>` from App.tsx. Extracted so the
 *   - optimistic-local-update step (`updateCategory`)
 *   - per-row enqueue step (`enqueueOp({ type: 'update_category', ... })`)
 * can be unit-tested with simple spies, without spinning up the full
 * dnd-kit + zustand harness. Mirrors Optimistic Local Reorder With OCC
 * Conflict Path + Decision: OCC integration — per-row rev passed to
 * update_category from the spec/design.
 */

import type { CategoryRow } from '@/lib/sheets/types';

export interface ReorderEnqueueOp {
  type: 'update_category';
  payload: {
    id: string;
    rev: number;
    patch: { sort_order: number };
  };
}

export interface ReorderDeps {
  /** Returns the current categories list. Read once, snapshotted before
   *  any local mutation, so each enqueued op carries the rev observed
   *  at drop time. */
  getCategories: () => readonly CategoryRow[];
  /** Optimistic local update — patches sort_order + updated_at. */
  applyLocal: (id: string, patch: { sort_order: number; updated_at: string }) => void;
  /** Sync queue enqueue. */
  enqueue: (op: ReorderEnqueueOp) => void;
  /** ISO timestamp factory (defaults to `new Date().toISOString()`). */
  now?: () => string;
}

export interface ReorderChange {
  id: string;
  sort_order: number;
}

/**
 * Apply a reorder change list. Snapshots the rev for every affected
 * row BEFORE any local mutation so each enqueued op carries the exact
 * rev observed at drop time. Rows whose `sort_order` is unchanged
 * MUST NOT appear in `changes` — caller (sortOrderMath.computeReorder)
 * is responsible for that.
 */
export function applyReorder(
  changes: ReadonlyArray<ReorderChange>,
  deps: ReorderDeps,
): void {
  if (changes.length === 0) return;

  const items = deps.getCategories();
  const snapshot = new Map<string, CategoryRow>();
  for (const ch of changes) {
    const row = items.find((c) => c.id === ch.id);
    if (row) snapshot.set(ch.id, row);
  }

  const now = (deps.now ?? (() => new Date().toISOString()))();

  for (const ch of changes) {
    const row = snapshot.get(ch.id);
    if (!row) continue;
    deps.applyLocal(ch.id, { sort_order: ch.sort_order, updated_at: now });
    deps.enqueue({
      type: 'update_category',
      payload: {
        id: ch.id,
        rev: row.rev,
        patch: { sort_order: ch.sort_order },
      },
    });
  }
}
